<template>
  <div
    class="form-item-table-cell-edit"
    :class="{ 'form-item-table-cell-edit--select': isSelectCell }"
  >
    <component
      :is="componentName"
      v-bind="formDataForEdit"
      @change="onChange"
    />
  </div>
</template>

<script>
import FormItemDatePicker from "../FormItem/FormItemDatePicker";
import FormItemTimePicker from "../FormItem/FormItemTimePicker";
import FormItemSpace from "../FormItem/FormItemSpace";
import FormItemDateTimePicker from "../FormItem/FormItemDateTimePicker";
import FormItemSelect from "../FormItem/FormItemSelect";
import FormItemRadioButtons from "../FormItem/FormItemRadioButtons";
import FormItemNumber from "../FormItem/FormItemNumber";
import FormItemShortText from "../FormItem/FormItemShortText";
import FormItemLongText from "../FormItem/FormItemLongText";
import FormItemUnsupported from "../FormItem/FormItemUnsupported";
import FormItemDateRangePicker from "../FormItem/FormItemDateRangePicker";
import FormItemDateMultiSelect from "../FormItem/FormItemDateMultiSelect";
import FormItemFlexibleTime from "../FormItem/FormItemFlexibleTime";
import enterpriseFormService from "@/services/enterprises/enterpriseFormService";
import operationReportService from "@/services/operations/operationReportService";
import {
  calcDefaultValue,
  calcNextFormItemValue,
  calcEditComponentName,
  getLatestReportValueForFormItem,
  formatReportValueForDisplay,
} from "@/utils/operations";
import isEmpty from "lodash/isEmpty";
import isString from "lodash/isString";
import { createNamespacedHelpers, mapGetters as mapRootGetters } from "vuex";
const { mapActions, mapGetters } = createNamespacedHelpers("operationReport");

export default {
  name: "FormItemTableCell",
  components: {
    FormItemDatePicker,
    FormItemTimePicker,
    FormItemSpace,
    FormItemDateTimePicker,
    FormItemSelect,
    FormItemRadioButtons,
    FormItemNumber,
    FormItemShortText,
    FormItemLongText,
    FormItemUnsupported,
    FormItemDateRangePicker,
    FormItemDateMultiSelect,
    FormItemFlexibleTime,
  },
  props: {
    enterpriseFormVersionItem: {
      type: Object,
      required: true,
    },
    reportFormVersionItem: {
      type: Object,
      required: true,
    },
    reportFormVersion: {
      type: Object,
      required: true,
    },
    updatable: {
      type: Boolean,
      default: false,
    },
    isEditing: {
      type: Boolean,
      default: false,
    },
    // 書き込み先 ao_enterprise_vessel_report_form_version.id
    reportFormVersionId: {
      type: String,
      required: true,
    },
    // 同一行内の「基準日」（左端ですでに入力済みの日付）があれば受け取る
    rowReportTargetDate: {
      type: [String, Date],
      required: false,
    },
    // 可変行テーブルの行番号。指定時は (form_version_item_id, repeat_index) で保存先を特定する
    repeatIndex: {
      type: Number,
      required: false,
      default: null,
    },
  },

  computed: {
    ...mapRootGetters({ masterMaps: "operationMasters/masterMaps" }),
    ...mapGetters({
      latestReportValueMap: "latestReportValueMap",
    }),
    componentName() {
      return calcEditComponentName(this.enterpriseFormVersionItem);
    },
    isSelectCell() {
      return this.componentName === "form-item-select";
    },
    name() {
      const {
        reportFormVersion,
        reportFormVersionItem,
        enterpriseFormVersionItem,
        updatable,
      } = this;
      const stateUpdatable =
        updatable && this.componentName !== "form-item-space"
          ? "enable"
          : "disable";
      const repeatSuffix =
        this.repeatIndex == null ? "" : `_r${this.repeatIndex}`;
      return `operation-report-form-${stateUpdatable}_${reportFormVersion.id}_${reportFormVersionItem.id}_${enterpriseFormVersionItem.id}${repeatSuffix}`;
    },
    disabled() {
      return !this.updatable;
    },
    label() {
      // テーブルセル内ではラベル非表示（ヘッダで表示済み）
      return "";
    },
    required() {
      return enterpriseFormService.getRequiredFromEnterpriseFormVersionItem(
        this.enterpriseFormVersionItem
      );
    },
    unit() {
      return enterpriseFormService.getUnit(this.enterpriseFormVersionItem);
    },
    selectOptions() {
      return enterpriseFormService.getSelectOptions(
        this.enterpriseFormVersionItem
      );
    },
    isNullableChecked() {
      return operationReportService.isNullableChecked(
        this.reportFormVersionItem
      );
    },
    nullableLabel() {
      return enterpriseFormService.getNullableLabelFromEnterpriseFormVersionItem(
        this.enterpriseFormVersionItem
      );
    },
    hasReportFormVersionItemValue() {
      return operationReportService.hasReportFormVersionItemValue(
        this.reportFormVersionItem
      );
    },
    isEdited() {
      return !isEmpty(this.reportFormVersionItem);
    },
    reportValue() {
      return operationReportService.getValueFromReportFormVersionItem(
        this.reportFormVersionItem
      );
    },
    reportFormVersionItemValueEdit() {
      const { reportValue, effectiveValueForEdit } = this;
      return effectiveValueForEdit !== undefined
        ? effectiveValueForEdit
        : reportValue;
    },
    // LATEST 対応
    valueFromLatestReport() {
      const { enterpriseFormVersionItem } = this;
      if (!enterpriseFormService.hasDefaultValue(enterpriseFormVersionItem))
        return null;
      const dv = enterpriseFormService.getDefaultValue(
        enterpriseFormVersionItem
      );
      if (!isString(dv) || dv.toUpperCase() !== "LATEST") return null;
      return getLatestReportValueForFormItem(this, enterpriseFormVersionItem);
    },
    isStoredValueLATEST() {
      const v = this.reportValue;
      return isString(v) && v.toUpperCase() === "LATEST";
    },
    shouldReplaceLATESTWithLatest() {
      const {
        isEditing,
        updatable,
        valueFromLatestReport,
        reportValue,
        isStoredValueLATEST,
      } = this;
      if (!isEditing || !updatable) return false;
      if (valueFromLatestReport == null) return false;
      if (isStoredValueLATEST) return true;
      if (reportValue == null || reportValue === "") return true;
      return false;
    },
    effectiveValueForEdit() {
      if (
        this.shouldReplaceLATESTWithLatest &&
        this.valueFromLatestReport != null
      ) {
        return this.valueFromLatestReport;
      }
      return undefined;
    },
    defaultValue() {
      const { enterpriseFormVersionItem, isEdited, disabled, isEditing } = this;
      if (disabled) return null;
      if (isEdited) return null;
      if (!isEditing) return null;
      return calcDefaultValue(this, enterpriseFormVersionItem);
    },
    formDataForEdit() {
      const {
        label,
        required,
        name,
        reportFormVersionItemValueEdit: initialValue,
        unit,
        nullableLabel,
        isNullableChecked,
        isEdited,
        hasReportFormVersionItemValue,
        isEditing,
        disabled,
        selectOptions,
        enterpriseFormVersionItem,
        defaultValue,
      } = this;
      return {
        label,
        required,
        name,
        initialValue,
        value: formatReportValueForDisplay(
          enterpriseFormVersionItem,
          this.reportValue,
          this.masterMaps
        ),
        unit,
        nullableLabel,
        isNullableChecked,
        isEdited,
        hasReportFormVersionItemValue,
        isEditing,
        disabled,
        selectOptions,
        enterpriseFormVersionItem,
        defaultValue,
        appendToBody: true,
        rowReportTargetDate: this.rowReportTargetDate,
      };
    },
  },

  methods: {
    ...mapActions({ setFormVersionItemValue: "setFormVersionItemValue" }),

    syncReplacedLATESTToStore() {
      const {
        shouldReplaceLATESTWithLatest,
        valueFromLatestReport,
        enterpriseFormVersionItem,
        reportFormVersionItem,
        reportFormVersionId,
      } = this;
      if (
        !shouldReplaceLATESTWithLatest ||
        valueFromLatestReport == null ||
        !reportFormVersionId
      )
        return;
      const payload = calcNextFormItemValue(
        enterpriseFormVersionItem,
        reportFormVersionItem,
        {
          value: valueFromLatestReport,
          nullableChecked:
            operationReportService.isNullableChecked(reportFormVersionItem) ||
            false,
        }
      );
      this.setFormVersionItemValue({ reportFormVersionId, ...payload });
    },

    onChange(value) {
      const {
        reportFormVersionId,
        enterpriseFormVersionItem,
        reportFormVersionItem,
      } = this;
      const nextFormItemValue = calcNextFormItemValue(
        enterpriseFormVersionItem,
        reportFormVersionItem,
        value
      );
      // 可変行テーブルのセルは repeat_index を付与して該当行に書き込む
      if (this.repeatIndex != null) {
        nextFormItemValue.repeat_index = this.repeatIndex;
      }
      this.setFormVersionItemValue({
        reportFormVersionId,
        ...nextFormItemValue,
      });
    },
  },

  watch: {
    shouldReplaceLATESTWithLatest: {
      handler(shouldReplace) {
        if (shouldReplace) this.syncReplacedLATESTToStore();
      },
      immediate: true,
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/aisea/_variables.scss";

.form-item-table-cell-edit {
  word-break: break-word;
  overflow-wrap: break-word;

  // テーブルセル内のフォーム項目はコンパクトに
  ::v-deep .operation-report-show-body-form-item {
    min-height: unset;
    padding: 0;
    row-gap: 2px;

    // ラベルを非表示（テーブルヘッダで代替）
    .form-item-label {
      display: none;
    }

    // エラー時は入力エリアに赤枠を表示（通常方式のラベル赤色と同様の視覚フィードバック）
    .form-item-label.is-editing.has-errors ~ .form-item-value-unit-edit {
      outline: 1px solid $aisea-red;
      border-radius: 4px;
    }

    // テキスト系inputはセル幅に合わせてword-breakを適用
    .vs-input--input {
      white-space: pre-wrap;
      word-break: break-word;
      overflow-wrap: break-word;
    }
  }

  // セレクトボックスのみ外のスタイルに合わせる（padding / row-gap / min-height を共通化）
  &.form-item-table-cell-edit--select {
    ::v-deep .form-item-value-unit-edit {
      padding-right: 0;
    }

    /* border-collapse: collapse を継承するため border-radius が効かないので明示指定 */
    ::v-deep .vue-treeselect__control {
      border-radius: 6px !important;
      border-collapse: separate !important;
    }
  }
}
</style>
